import React, {useState, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import queryString from 'query-string';

import StyledCustomize from './StyledCustomize';
import SelectColumnsForm from './SelectColumnsForm';
import CustomizeTableAside from './CustomizeTableAside';

import {labels} from '../../../../util/utils';
import {projectSessionKeys, StandardColumns, standardColumnsExternalUser} from '../../../../util/uiconstants';
import {customColumnsIcon} from '../../../../util/svgIcons';
import {getCustomUserColumn} from './utils';

import {loadStorage, saveStorage} from '../../../Common/localStorage';
import ErrorSummary from '../../../Common/ErrorSummary/ErrorSummary';

import {setUserColumn} from '../../../../actions/ContactDB';
import {updateDisplayData} from '../../../../actions/Metrics/dashboardContextAction';
import {updateProjectCustomColumns} from '../../../../actions/Project/projectAction';
import {clearErrors} from '../../../../actions/erroractions';
import {setIsModalOpen} from '../../../../actions/AccessGroups/SetIsModalOpen';
import Modal from '../../../CoreWrappers/Modal';
import ToolbarButton from '../Toolbar/ToolbarButton';

const CustomizeTableModal = () => {
	const [open, setOpen] = useState(false);
	const [shouldUpdate, setShouldUpdate] = useState(false);
	const dispatch = useDispatch();
	const customColumnsSelector = useSelector(({project}) => ({
		customColumns: project?.customColumns ?? []
	}));
	const [userColumns, setUserColumns] = useState();
	const [isLoading, setIsLoading] = useState(true);
	const [editMode, setEditMode] = useState(null);
	const query = queryString.parse(location.search.toLowerCase());
	const projectId = query?.projectid ? query.projectid : '';
	const projectSessionKey = `${projectSessionKeys.userColumns}_${projectId}`;
	const selectedColumns = loadStorage(projectSessionKey);
	const setModalErrors = (val) => dispatch(clearErrors()).then(() => dispatch(setIsModalOpen(val)));
	const entities = useSelector((state) => state.contacts.entities);
	const engagements = useSelector(({dashboardContext}) => dashboardContext?.engagements);
	const orgChart = useSelector(({dashboardContext}) => dashboardContext?.orgChart);
	const maxLengthCustomColumns = 5;

	const project = useSelector((store) => store.project);
	const userPreferences = useSelector((store) => store.userPreferences);

	const isProjectOwner = useMemo(
		() => project.users?.some((user) => user.isProjectOwner && user.userId === userPreferences.userId) ?? false,
		[project, userPreferences]
	);

	const isInternal = useMemo(
		() =>
			project.users?.filter((user) => user.userType === 'Member' && user.userId === userPreferences.userId) ?? false,
		[project, userPreferences]
	);

	useEffect(() => {
		if (project && !_.isEmpty(project) && !_.isEmpty(project.users)) {
			let customColumns = [...(project?.customColumns ?? [])];
			let columns = [];
			if (selectedColumns?.length) {
				//if users are Internal they use StandardColumns,but if they are not Internal they use standardColumnsExternalUser
				if (isInternal?.length > 0) {
					columns = [
						...selectedColumns.filter(
							(selected) =>
								StandardColumns.columns.some((standard) => standard.labels == selected.labels) ||
								customColumns.some((custom) => custom.name == selected.labels)
						)
					];
				} else {
					columns = [
						...selectedColumns.filter(
							(selected) =>
								standardColumnsExternalUser.columns.some((standard) => standard.labels == selected.labels) ||
								customColumns.some((custom) => custom.name == selected.labels)
						)
					];
				}
				customColumns = customColumns.filter(
					(custom) => !selectedColumns.some((selected) => selected.labels == custom.name)
				);
			} else {
				columns = isInternal?.length > 0 ? [...StandardColumns.columns] : [...standardColumnsExternalUser.columns];
			}

			const userCustomColumns = customColumns.map((col) =>
				getCustomUserColumn(
					col.id,
					col.apiId,
					col.name,
					col.options.map((option) => option),
					col.isVisibleForExternals
				)
			);
			columns = [...columns, ...userCustomColumns];
			setShouldUpdate(true);
			setUserColumns([...columns]);
			saveStorage(projectSessionKey, [...columns]);
			setIsLoading(false);
		}
	}, [project.customColumns]);

	useEffect(() => {
		if (shouldUpdate) {
			saveOnOk();
		}
	}, [shouldUpdate]);

	const showModal = (isOpen) => {
		setModalErrors(isOpen);
		setOpen(isOpen);
	};

	const saveOnOk = () => {
		let ccUC = customColumnsSelector.customColumns.map((userCol) => {
			let cCS = userColumns.find((customColum) => customColum.id === userCol.id);
			if (cCS) {
				return {...cCS};
			}
			let attributes = userCol.options?.map((a) => {
				return {apiId: a.id, id: a.id, option: a.option};
			});
			return {
				apiId: userCol.id,
				id: userCol.id,
				attributes: attributes,
				isChecked: userColumns?.find((x) => x.label == userCol.name)?.isChecked,
				isCustom: true,
				label: userCol.name,
				labels: userCol.name,
				propName: 'custom',
				sortable: false,
				twoLineBreak: true,
				isVisibleForExternals: userCol.isVisibleForExternals
			};
		});
		let allUserColumns = userColumns.filter((x) => !x.isCustom);
		ccUC.forEach((col) => allUserColumns.push(col));
		dispatch(setUserColumn(allUserColumns)).then(() => {
			let newEntities = [...entities].map((entity) => {
				return {
					...entity,
					contacts: entity.contacts.map((entityContact) => {
						return {
							...entityContact,
							customColumns: entityContact.customColumns
								.map((contactCustomColumn) => {
									const projectCustomColumn = project.customColumns.find(
										(customColum) => customColum.id === contactCustomColumn.id
									);
									//it checks if the user has the custom column
									if (!projectCustomColumn) {
										if (allUserColumns.find((col) => col.id === contactCustomColumn.id)) {
											return {...contactCustomColumn};
										} else {
											return undefined;
										}
									} else {
										//it searches the option that user has assigned on projectCustomColumn, which is custom columns list on redux.
										let contactOptionAssigned = projectCustomColumn.options.find(
											(option) => option.id == contactCustomColumn.options[0]?.id
										);

										//it assigns the values found or with the validation, a value empty; and deleting it (old label on option) from contactList (table)
										return {
											...contactCustomColumn,
											name: projectCustomColumn.name,
											options: [
												{
													...contactOptionAssigned,
													name: contactOptionAssigned?.option,
													option: contactOptionAssigned?.option
												}
											]
										};
									}
								})
								.filter((e) => e !== undefined)
						};
					})
				};
			});
			dispatch(updateDisplayData(engagements, newEntities, orgChart));
		});
		setUserColumns([...allUserColumns]);
		saveStorage(projectSessionKey, allUserColumns);
		setIsLoading(false);
		setEditMode(null);
		showModal(false);
	};

	const renderTrigger = () => {
		const handlerOnOpenClick = () => {
			showModal(true);
		};

		const props = {
			svg: customColumnsIcon,
			label: labels.toolbarCustomizeTable,
			onClick: handlerOnOpenClick
		};

		return <ToolbarButton {...props} />;
	};

	const renderModal = () => {
		const changeModeHandler = (value) => {
			setEditMode(value);
		};

		const handlerOnSubmit = () => {
			if (isProjectOwner && !isLoading) {
				const customColumns = userColumns
					.filter((x) => x.isCustom)
					.map((userCol) => {
						return {
							id: userCol?.apiId,
							name: userCol.label,
							options: userCol.attributes?.map((a) => {
								return {id: a?.id, option: a.option};
							}),
							columnType: 'radio',
							isVisibleForExternals: userCol.isVisibleForExternals,
							isChecked: userCol.isChecked
						};
					});

				dispatch(updateProjectCustomColumns(projectId, customColumns))
					.then(() => {
						setShouldUpdate(false);
					})
					.finally(() => setIsLoading(false));
			} else {
				dispatch(setUserColumn(userColumns)).then(() => {
					saveStorage(projectSessionKey, userColumns);
					setEditMode(null);
					setIsLoading(false);
					showModal(false);
				});
			}
		};

		const onHandleSaveChanges = () => {
			// saveStorage.
			setIsLoading(true);

			let untouchedColumns = loadStorage(projectSessionKey);
			const untouchedCustomColumnsHash = untouchedColumns //.filter(c => c.isCustom)
				.reduce((acc, column) => {
					acc[column.id] = column.isChecked;
					return acc;
				}, {});

			saveStorage(projectSessionKey, userColumns);

			let touchedCustomColumns = userColumns //.filter(c => c.isCustom)
				.reduce((acc, column) => {
					acc[column.id] = column.isChecked;
					return acc;
				}, {});

			const isEqual = areHashesEqual(touchedCustomColumns, untouchedCustomColumnsHash);
			if (!isEqual || shouldUpdate) {
				// Submit the API, if we have changes.
				handlerOnSubmit();
			} else {
				setIsLoading(false);
			}
		};

		const onHandlerCancelChanges = () => {
			//TODO: improve to not repeat code
			let customColumns = [...project.customColumns] || [];
			let columns = [];

			if (selectedColumns?.length) {
				//if users are isInternal they use StandardColumns,but if they are not isInternal they use standardColumnsExternalUser
				if (isInternal) {
					columns = [
						...selectedColumns.filter(
							(selected) =>
								StandardColumns.columns.some((standard) => standard.labels == selected.labels) ||
								customColumns.some((custom) => custom.name == selected.labels)
						)
					];
				} else {
					columns = [
						...selectedColumns.filter(
							(selected) =>
								standardColumnsExternalUser.columns.some((standard) => standard.labels == selected.labels) ||
								customColumns.some((custom) => custom.name == selected.labels)
						)
					];
				}
				customColumns = customColumns.filter(
					(custom) => !selectedColumns.some((selected) => selected.labels == custom.name)
				);
			} else {
				columns = isInternal ? [...StandardColumns.columns] : [...standardColumnsExternalUser.columns];
			}
			const userCustomColumns = customColumns.map((col) =>
				getCustomUserColumn(
					col.id,
					col.apiId,
					col.name,
					col.options.map((option) => option)
				)
			);
			columns = [...columns, ...userCustomColumns];
			setUserColumns([...columns]);
			setIsLoading(false);
			setEditMode(null);
			showModal(false);
		};

		function areHashesEqual(hash1, hash2) {
			const keys1 = Object.keys(hash1);
			const keys2 = Object.keys(hash2);

			// Check if the number of properties is the same
			if (keys1.length !== keys2.length) {
				return false;
			}

			// Check if the values are the same
			for (const key of keys1) {
				if (hash1[key] !== hash2[key]) {
					return false; // If values differ, return false
				}
			}

			// If we haven't returned false yet, the hashes are equal
			return true;
		}

		const modalProps = {
			show: open,
			headerText: labels.customizeTableModal,
			labels: {
				modalConfirmBtnLabel: labels.saveCustomizeTable,
				modalCancelBtnLabel: labels.cancelCustomizeTable,
				modalCloseBtnTitletip: labels.closeTitleModal
			},
			modalsize: isProjectOwner ? 'medium' : 'small',
			onClose: onHandlerCancelChanges,
			onOkClick: onHandleSaveChanges,
			onCancelClick: onHandlerCancelChanges
		};

		return (
			<Modal {...modalProps}>
				<ErrorSummary />

				<StyledCustomize isProjectOwner={isProjectOwner}>
					<SelectColumnsForm
						isLoading={isLoading}
						projectId={projectId}
						userColumns={userColumns}
						setUserColumns={setUserColumns}
						changeMode={changeModeHandler}
					/>
					{isProjectOwner && (
						<CustomizeTableAside
							isDisabled={userColumns?.filter((x) => x.isCustom).length >= maxLengthCustomColumns && editMode === null}
							userColumns={userColumns}
							setUserColumns={setUserColumns}
							editMode={editMode}
							changeMode={changeModeHandler}
						/>
					)}
				</StyledCustomize>
			</Modal>
		);
	};

	return (
		<>
			{renderTrigger()}
			{renderModal()}
		</>
	);
};

CustomizeTableModal.propTypes = {};

export default CustomizeTableModal;
